import React, { useEffect, useState, useCallback } from "react";

const ContentProtector = () => {
  const [showScreenshotAlert, setShowScreenshotAlert] = useState(false);

  const triggerScreenshotAlert = useCallback(() => {
    setShowScreenshotAlert(true);

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText("").catch(() => {
        // Silently ignore — clipboard permission not granted
      });
    }
  }, []);

  const dismissAlert = () => setShowScreenshotAlert(false);

  // Inject CSS to disable text selection globally but EXEMPT inputs/textareas
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "content-protector-style";
    style.innerHTML = `
      * {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        -webkit-touch-callout: none !important;
      }
      /* Allow selection and interaction in input fields and textareas */
      input, textarea, [contenteditable="true"] {
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const el = document.getElementById("content-protector-style");
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      // Allow right-click on inputs if needed, or keep disabled for maximum security
      // To allow right-click only on inputs: if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();
    };

    // Prevent text selection
    const handleSelection = (e) => {
      const tag = e.target.tagName;
      // Don't prevent selection if user is interacting with an input or textarea
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      if (window.getSelection().toString().length > 0) {
        e.preventDefault();
      }
    };

    // Prevent copy
    const handleCopy = (e) => {
      e.preventDefault();
    };

    // Prevent cut
    const handleCut = (e) => {
      e.preventDefault();
    };

    // ALLOW paste ONLY into inputs and textareas
    const handlePaste = (e) => {
      const tag = e.target.tagName;
      const isEditable = e.target.isContentEditable;
      
      if (tag === "INPUT" || tag === "TEXTAREA" || isEditable) {
        // Allow the paste to happen naturally
        return;
      } else {
        // Block paste for everything else
        e.preventDefault();
      }
    };

    // Prevent drag/drop
    const handleDragStart = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
    };

    // Enhanced key blocking
    const preventKeys = (e) => {
      const isPrintScreen = e.key === "PrintScreen";
      const isMacScreenshot =
        e.metaKey && e.shiftKey && ["3", "4", "5"].includes(e.key);
      const isWinScreenshot = e.metaKey && e.shiftKey && (e.key === "S" || e.key === "s");

      const blockedCombos =
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && ["I", "C", "J"].includes(e.key)) ||
        (e.ctrlKey && e.key === "U") ||
        (e.ctrlKey && e.key === "C") || // block Ctrl+C explicitly
        (e.ctrlKey && e.key === "X") || // block Ctrl+X explicitly
        (e.metaKey && e.altKey && e.key === "I") || // Mac dev tools
        isPrintScreen ||
        isMacScreenshot ||
        isWinScreenshot ||
        (e.ctrlKey && e.key === "P");

      if (blockedCombos) {
        e.preventDefault();
        e.stopPropagation();

        if (isPrintScreen || isMacScreenshot || isWinScreenshot) {
          triggerScreenshotAlert();
        }
        return false;
      }
    };

    // Add all event listeners
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("selectstart", handleSelection);
    document.addEventListener("copy", handleCopy);
    document.addEventListener("cut", handleCut);
    document.addEventListener("paste", handlePaste);
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("keydown", preventKeys, true);
    document.addEventListener("keyup", preventKeys, true);

    // Anti-screenshot overlay
    const overlay = document.createElement("div");
    overlay.id = "anti-screen-overlay";
    Object.assign(overlay.style, {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      zIndex: "9999",
      pointerEvents: "none",
      background: "rgba(255,255,255,0.01)",
    });
    document.body.appendChild(overlay);

    // Protect all images
    const protectImages = () => {
      const allImages = document.querySelectorAll("img");
      allImages.forEach((img) => {
        img.setAttribute("draggable", "false");
        img.style.pointerEvents = "none";
        img.style.userSelect = "none";
        img.style.webkitUserDrag = "none";
      });
    };

    protectImages();
    const observer = new MutationObserver(protectImages);
    observer.observe(document.body, { subtree: true, childList: true });

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("selectstart", handleSelection);
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("cut", handleCut);
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("keydown", preventKeys, true);
      document.removeEventListener("keyup", preventKeys, true);
      observer.disconnect();
      const el = document.getElementById("anti-screen-overlay");
      if (el) el.remove();
    };
  }, [triggerScreenshotAlert]);

  return (
    <>
      {showScreenshotAlert && (
        <div className="fixed inset-0 z-[999999] bg-white backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-in slide-in-from-bottom duration-500">
            
            {/* Danger Icon Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-t-2xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-black/10 backdrop-blur-sm"></div>
              <div className="relative flex items-center justify-center">
                <div className="bg-white/20 backdrop-blur-xl rounded-full p-4 animate-pulse">
                  <svg
                    className="w-12 h-12 text-white drop-shadow-lg"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <div className="flex items-center justify-center mb-4">
                <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  Security Alert
                </span>
              </div>
              
              <h2 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent mb-3 text-center">
                Screenshot Detected!
              </h2>
              
              <p className="text-gray-600 text-center mb-6 leading-relaxed">
                This page contains <span className="font-semibold text-red-600">highly confidential</span> information. 
                Screenshots, screen recordings, and any form of capture are <span className="font-semibold">strictly prohibited</span>.
              </p>

              {/* Warning Box */}
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-6 shadow-sm">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-red-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700 font-medium">
                      Violation Notice
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      Unauthorized capture may result in legal action and immediate account termination
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={dismissAlert}
                  className="w-full bg-gradient-to-r from-gray-800 to-black text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center group"
                >
                  <span>I Understand</span>
                  <svg 
                    className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                
                <p className="text-xs text-center text-gray-500">
                  By clicking "I Understand", you agree to comply with our security policy
                </p>
              </div>
            </div>

            <div className="h-2 bg-gradient-to-r from-red-500 via-red-600 to-red-500 rounded-b-2xl"></div>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(ContentProtector);